import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import toast from "react-hot-toast";
import { useCreditItemsMutation } from "../../Store/Apis/bookingApi";

const CreditNoteModal = ({ show, onHide, order, onCreditSuccess }) => {
  const [creditItems, setCreditItems] = useState([]);
  const [creditItemsAPI, { isLoading: isCreditingItems }] =
    useCreditItemsMutation();
  const [creditNoteNumber, setCreditNoteNumber] = useState("");

  // Generate credit note number on open or when order changes
  useEffect(() => {
    if (show && order) {
      const outlet = order.outletID || order.outletId;
      const prefix = outlet === 4 ? "CNWF" : "CNGH"; // default to CNGH (outlet 3)
      const random = Math.floor(1000 + Math.random() * 9000); // 4 digits
      setCreditNoteNumber(`${prefix}${random}`);
    }
  }, [show, order]);

  useEffect(() => {
    if (!order) return;

    const sourceItems = order.orderDetails?.length
      ? order.orderDetails
      : order.services?.map((s) => ({
          // Map services shape to expected orderDetail-like shape
          itemID: s.serviceId,
          serviceID: s.serviceId,
          serviceName:
            s.productServiceName || s.serviceName || s.service_type_name,
          quantity: s.serviceQuantity,
          rate: s.servicePrice,
          unitPrice: s.servicePrice,
          roomId: s.roomId,
          creditedAmount: s.creditedAmount || 0,
        })) || [];

    if (sourceItems.length) {
      const items = sourceItems.map((item) => {
        const originalQuantity = item.quantity || item.serviceQuantity || 1;
        const originalRate =
          item.rate || item.unitPrice || item.servicePrice || 0;
        const fullLineAmount = originalQuantity * originalRate;
        const previousCreditedAmount = Number(
          item.creditedAmount || item.previousCreditedAmount || 0
        );
        const remainingAmount = Math.max(
          0,
          fullLineAmount - previousCreditedAmount
        );
        return {
          ...item,
          originalQuantity,
          originalRate,
          roomId: item.roomId || item.roomID || null,
          paidAmount: item.paidAmount || item.amountPaid || 0, // placeholder; will be overwritten by proportional distribution below
          creditQuantity: 0,
          creditAmount: 0,
          selected: false,
          previousCreditedAmount,
          fullLineAmount,
          remainingAmount,
        };
      });
      // Proportionally distribute overall paid total across lines if we have an order-level paidTotal
      const overallPaid = Number(order?.paidTotal) || 0;
      const aggregateFull = items.reduce((a, it) => a + it.fullLineAmount, 0);
      if (overallPaid > 0 && aggregateFull > 0) {
        items.forEach((it, idx) => {
          // Last item gets residual to avoid rounding drift
          if (idx === items.length - 1) {
            const distributedSoFar = items
              .slice(0, -1)
              .reduce((a, p) => a + p.paidAmount, 0);
            it.paidAmount = Math.max(0, overallPaid - distributedSoFar);
          } else {
            it.paidAmount = (overallPaid * it.fullLineAmount) / aggregateFull;
          }
        });
      }
      setCreditItems(items);
    } else {
      setCreditItems([]);
    }
  }, [order]);

  const handleItemSelection = (index, selected) => {
    setCreditItems((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        selected,
        creditQuantity: 0,
        creditAmount: 0,
      };
      return updated;
    });
  };

  const handleQuantityChange = (index, quantity) => {
    setCreditItems((prev) => {
      const updated = [...prev];
      const item = updated[index];
      const maxQuantity = item.originalQuantity;
      const validQuantity = Math.min(Math.max(0, quantity), maxQuantity);
      // Proposed amount based on quantity cannot exceed remaining
      const proposedAmount = validQuantity * item.originalRate;
      const boundedAmount = Math.min(proposedAmount, item.remainingAmount);
      updated[index] = {
        ...item,
        creditQuantity: validQuantity,
        creditAmount: boundedAmount,
      };
      return updated;
    });
  };

  const handleAmountChange = (index, amount) => {
    setCreditItems((prev) => {
      const updated = [...prev];
      const item = updated[index];
      const maxAmount = item.remainingAmount; // enforce remaining limit
      const validAmount = Math.min(Math.max(0, amount), maxAmount);

      updated[index] = {
        ...item,
        creditAmount: validAmount,
        creditQuantity:
          validAmount > 0 ? Math.ceil(validAmount / item.originalRate) : 0,
      };
      return updated;
    });
  };

  const calculateTotalCredit = () => {
    return creditItems.reduce((total, item) => {
      return total + (item.selected ? item.creditAmount : 0);
    }, 0);
  };

  // Calculate dynamic remaining pool (what still needs credit to reconcile overpayment)
  const calculateRemainingPool = () => {
    const amountOwedRaw = Number(order?.amountOwedRaw || 0); // positive => overpaid or owed? In detail component definition paid - subtotal
    // If amountOwedRaw <= 0, there is no overpayment to credit (it's either owed or exactly balanced)
    if (amountOwedRaw <= 0) return 0;
    const previouslyCredited = calculatePreviouslyCredited();
    const currentDraftCredit = calculateTotalCredit();
    // remaining overpayment that has not yet been credited (previously or in this draft)
    return Math.max(0, amountOwedRaw - previouslyCredited - currentDraftCredit);
  };

  const calculatePreviouslyCredited = () =>
    creditItems.reduce(
      (sum, item) => sum + (item.previousCreditedAmount || 0),
      0
    );

  const getSelectedItems = () => {
    return creditItems.filter((item) => item.selected && item.creditAmount > 0);
  };

  const deriveRoomId = (selectedItems) => {
    // Priority: selected items first
    for (const it of selectedItems) {
      if (it.roomId || it.roomID) return it.roomId || it.roomID;
    }
    // Fallback: from services collection
    if (order?.services?.length) {
      for (const s of order.services) {
        if (s.roomId) return s.roomId;
      }
    }
    // Fallback: from orderDetails
    if (order?.orderDetails?.length) {
      for (const d of order.orderDetails) {
        if (d.roomId) return d.roomId;
      }
    }
    return null; // explicit null if none
  };

  const handleMakeCreditNote = async () => {
    const selectedItems = getSelectedItems();

    if (selectedItems.length === 0) {
      toast.error("Please select at least one item for credit note");
      return;
    }

    try {
      const roomId = deriveRoomId(selectedItems);
      if (!roomId) {
        console.warn(
          "No roomId could be derived from order or selected items",
          {
            orderId: order.orderId || order.orderID,
            selectedItems,
            services: order?.services,
            orderDetails: order?.orderDetails,
          }
        );
        toast.error("Room ID not found for this order. Cannot credit items.");
        return;
      }

      const creditItemsData = {
        orderId: order.orderId || order.orderID,
        roomId: roomId,
        creditedAmount: calculateTotalCredit(),
      };

      console.log("Credit Items Data:", creditItemsData);

      await creditItemsAPI(creditItemsData).unwrap();

      toast.success("Items credited successfully!");
      if (typeof onCreditSuccess === "function") {
        onCreditSuccess();
      }
      onHide();

      // Reset form
      setCreditItems((prev) =>
        prev.map((item) => ({
          ...item,
          selected: false,
          creditQuantity: 0,
          creditAmount: 0,
        }))
      );
    } catch (error) {
      console.error("Error crediting items:", error);
      toast.error(
        error?.data?.message || "Failed to credit items. Please try again."
      );
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title className="d-flex flex-column">
          <span>
            Create Credit Note - Order #{order?.orderID || order?.orderId}
          </span>
          {creditNoteNumber && (
            <small className="text-muted">
              Credit Note #: {creditNoteNumber}
            </small>
          )}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* <div className="mb-4">
          <label className="form-label">
            <strong>
              Reason for Credit Note <span className="text-danger">*</span>
            </strong>
          </label>
          <textarea
            className="form-control"
            rows="3"
            placeholder="Please provide a detailed reason for this credit note..."
            value={creditReason}
            onChange={(e) => setCreditReason(e.target.value)}
            required
          />
        </div> */}

        <div className="mb-4">
          <h6 className="text-primary mb-3">Select Items for Credit Note</h6>
          <div className="table-responsive">
            <table className="table table-bordered">
              <thead className="thead-light">
                <tr>
                  <th width="50">Select</th>
                  <th>Item</th>
                  <th>Guest / Room</th>
                  <th>Check In</th>
                  <th>Check Out</th>
                  <th className="text-center">Original Qty</th>
                  <th className="text-end text-danger">Prev Credited</th>
                  <th className="text-end">Remaining To Credit</th>
                  <th className="text-center">Credit Qty</th>
                  <th className="text-end">Paid Amount</th>
                  <th className="text-end">Credit Amount</th>
                </tr>
              </thead>
              <tbody>
                {creditItems.map((item, index) => (
                  <tr key={index}>
                    <td className="text-center">
                      <button
                        type="button"
                        className={`btn btn-sm ${
                          item.selected
                            ? "btn-success"
                            : "btn-outline-secondary"
                        }`}
                        onClick={() =>
                          handleItemSelection(index, !item.selected)
                        }
                        style={{ minWidth: "32px" }}
                        aria-label={item.selected ? "Selected" : "Select"}
                      >
                        {item.selected ? "✓" : ""}
                      </button>
                    </td>
                    <td>
                      <div>
                        <strong>
                          {item.serviceName ||
                            item.productName ||
                            item.itemName}
                        </strong>
                        {item.description && (
                          <div className="text-muted small">
                            {item.description}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="small">
                      <div>{item.guestName || "-"}</div>
                    </td>
                    <td className="small">
                      {item.checkIn
                        ? new Date(item.checkIn).toLocaleDateString()
                        : "-"}
                    </td>
                    <td className="small">
                      {item.checkOut
                        ? new Date(item.checkOut).toLocaleDateString()
                        : "-"}
                    </td>
                    <td className="text-center">
                      <span className="badge bg-info">
                        {item.originalQuantity}
                      </span>
                    </td>
                    <td className="text-end">
                      {item.previousCreditedAmount > 0
                        ? `R ${item.previousCreditedAmount.toFixed(2)}`
                        : "-"}
                    </td>
                    <td className="text-end">
                      {(() => {
                        // Distribute remaining pool proportionally by full line amount to show indicative remaining
                        const pool = calculateRemainingPool();
                        const totalFull = creditItems.reduce(
                          (a, it) => a + it.fullLineAmount,
                          0
                        );
                        if (pool === 0 || totalFull === 0) return "R 0.00";
                        const proportional =
                          (pool * item.fullLineAmount) / totalFull;
                        return `R ${proportional.toFixed(2)}`;
                      })()}
                    </td>
                    <td className="text-center">
                      <input
                        type="number"
                        className="form-control form-control-sm text-center"
                        style={{ width: "80px", margin: "0 auto" }}
                        min="0"
                        max={item.originalQuantity}
                        value={
                          item.creditQuantity === 0 ? "" : item.creditQuantity
                        }
                        onChange={(e) => {
                          const value =
                            e.target.value === ""
                              ? 0
                              : parseInt(e.target.value);
                          handleQuantityChange(index, value);
                        }}
                        disabled={!item.selected}
                      />
                    </td>
                    <td className="text-end">
                      R {Number(item.paidAmount || 0).toFixed(2)}
                    </td>
                    <td className="text-end">
                      <input
                        type="number"
                        className="form-control form-control-sm text-end"
                        style={{ width: "120px", marginLeft: "auto" }}
                        step="0.01"
                        min="0"
                        max={item.remainingAmount}
                        value={item.creditAmount === 0 ? "" : item.creditAmount}
                        onChange={(e) => {
                          // If empty, set to 0, otherwise parse as float
                          const value =
                            e.target.value === ""
                              ? 0
                              : parseFloat(e.target.value);
                          handleAmountChange(index, value);
                        }}
                        disabled={!item.selected}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="row">
          <div className="col-md-8"></div>
          <div className="col-md-4">
            <div
              className="card"
              style={{ background: "#FFB84D", border: "1px solid #e0a135" }}
            >
              <div className="card-body py-3 md-7">
                <h6 className="mb-3" style={{ color: "#3a2a00" }}>
                  Credit Note Summary
                </h6>
                <div className="d-flex justify-content-between">
                  <span style={{ fontWeight: 500 }}>Selected Items:</span>
                  <span style={{ fontWeight: 600 }}>
                    {getSelectedItems().length}
                  </span>
                </div>
                <div className="d-flex justify-content-between">
                  <span style={{ fontWeight: 500 }}>Previously Credited:</span>
                  <span style={{ fontWeight: 600 }}>
                    R {calculatePreviouslyCredited().toFixed(2)}
                  </span>
                </div>
                <div className="d-flex justify-content-between">
                  <strong style={{ color: "#3a2a00" }}>New Credit:</strong>
                  <strong style={{ color: "#3a2a00" }}>
                    R {calculateTotalCredit().toFixed(2)}
                  </strong>
                </div>
                <hr className="my-2" />
                <div className="d-flex justify-content-between">
                  <strong style={{ color: "#3a2a00" }}>Total Credited:</strong>
                  <strong style={{ color: "#3a2a00" }}>
                    R{" "}
                    {(
                      calculatePreviouslyCredited() + calculateTotalCredit()
                    ).toFixed(2)}
                  </strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onHide}
          disabled={isCreditingItems}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-warning"
          onClick={handleMakeCreditNote}
          disabled={isCreditingItems || getSelectedItems().length === 0}
        >
          {isCreditingItems ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
              ></span>
              Crediting Items...
            </>
          ) : (
            <>
              <i className="fas fa-receipt me-1"></i>
              Credit Items
            </>
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default CreditNoteModal;
